import React, { useId } from 'react';
import styled from 'styled-components';

import { getBaseUnit, getColor } from '../styles/themeGetters';
import { Box } from '../Box';
import { Text } from '../Text';
import { FontSizeKeys, FontWeightKeys } from '../styles/themes';

// size xs only affects label text size, everything else is the same as size s
type Size = 'xs' | 's' | 'l';

type Variant = 'default' | 'disabled';

const labelFontSizes: { [key in Size]: FontSizeKeys } = {
  xs: 14,
  s: 14,
  l: 20,
};

type ToggleProps = {
  variant?: Variant;
  size: Size;
  label?: string;
  labelPosition?: 'left' | 'right';
  fontWeight?: FontWeightKeys;
  checked: boolean;
  onToggle: () => void;
  'aria-label'?: string;
};

const Switch = styled.div<{ size: Size }>`
  position: absolute;
  top: ${({ size }) => (size === 'l' ? '3px' : '2px')};
  left: ${({ size }) => (size === 'l' ? '3px' : '2px')};
  height: ${({ size }) => (size === 'l' ? '18px' : '12px')};
  width: ${({ size }) => (size === 'l' ? '18px' : '12px')};
  border-radius: 100%;
  background-color: ${getColor('white')};
  transition: transform 0.3s ease-in-out;
`;

const Checkbox = styled.input<{
  switchSize: Size;
}>`
  /* opacity set to 0 so that this input can be focused.
     by being focused, focus-within can be applied on its parent. */
  opacity: 0;
  cursor: pointer;
  &:checked + ${Switch} {
    transform: ${({ switchSize }) =>
      `translateX(${switchSize === 'l' ? 18 : 12}px)`};
  }
`;

const ToggleWrapper = styled.label<
  { size: Size } & {
    isToggled: boolean;
    isDisabled: boolean;
  }
>`
  cursor: pointer;
  position: relative;
  display: inline-block;
  width: ${({ size }) => (size === 'l' ? '42px' : '28px')};
  height: ${({ size }) => (size === 'l' ? '24px' : '16px')};
  border-radius: 16px;
  background-color: ${({ isDisabled, isToggled }) => {
    if (isToggled) {
      return getColor('navy');
    }
    if (isDisabled) {
      return getColor('grey-light-100');
    }
    return getColor('grey');
  }};

  &:focus-within {
    box-shadow: ${({ isDisabled }) =>
      isDisabled ? 'none' : `0 0 0 2px ${getColor('blue-light')}`};
  }
`;

const Toggle = ({
  variant = 'default',
  size = 's',
  label,
  fontWeight,
  checked,
  onToggle,
  labelPosition = 'right',
  'aria-label': ariaLabel,
}: ToggleProps) => {
  const handleChange = () => variant !== 'disabled' && onToggle();

  const id = useId();

  return (
    <Box
      display="flex"
      flexDirection={labelPosition === 'left' ? 'row-reverse' : 'row'}
      alignItems="center"
      justifyContent="flex-start"
      flexGap={getBaseUnit(3)}
    >
      <ToggleWrapper
        size={size}
        isToggled={checked}
        isDisabled={variant === 'disabled'}
      >
        <Checkbox
          type="checkbox"
          onChange={handleChange}
          switchSize={size}
          disabled={variant === 'disabled'}
          checked={checked}
          id={id}
          aria-label={ariaLabel}
        />
        <Switch size={size} />
      </ToggleWrapper>

      {label && (
        <Text fontSize={labelFontSizes[size]} fontWeight={fontWeight}>
          <label htmlFor={id}>{label}</label>
        </Text>
      )}
    </Box>
  );
};

export { Toggle };
